import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal-on-scroll primitive for the landing page. `useReveal` returns a ref to
 * attach and a `visible` flag that flips true once the element scrolls into
 * view. Respects `prefers-reduced-motion` (visible immediately, no transition)
 * and degrades to visible when IntersectionObserver is unavailable, so nothing
 * ever stays invisible. The scoring card uses the hook directly to drive its
 * ring/bar fills; everything else wraps content in <Reveal>.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Stagger, in ms, applied to the transition once visible. */
  delayMs?: number;
}

/** Fades + lifts its children into view on scroll. */
export function Reveal({ children, className = '', delayMs = 0 }: RevealProps) {
  // Resolved synchronously on first render (client-only SPA) so reduced-motion
  // users get the final layout with no transition and no flash-in.
  const [reduce] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const { ref, visible } = useReveal<HTMLDivElement>();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(22px)',
        transition:
          'opacity .6s cubic-bezier(.2,.8,.2,1), transform .6s cubic-bezier(.2,.8,.2,1)',
        transitionDelay: visible ? `${delayMs}ms` : '0ms',
      }}
    >
      {children}
    </div>
  );
}
